import React, { useEffect, useRef } from "react";
import analytics from "../../services/analytics";
import { usePageTitle } from "../../hooks/usePageTitle";

// Where we send users to share why they left. Feedback is collected in the form;
// this page exists only to record the uninstall and hand off.
const FEEDBACK_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSfVXsktjg4CmCpryA4Gbg7kiW0OYupHu1Xw2zc-H2GdjNNtQA/viewform";

// Small delay so analytics flushes and the user sees the handoff (avoids a jarring
// instant jump that can read as a broken page).
const REDIRECT_DELAY_MS = 900;

const UninstallFeedback = () => {
  usePageTitle("Uninstalled");

  // Fire the ballpark uninstall event once, then redirect to the feedback form.
  // The ref only guards the analytics call against React 18 StrictMode's
  // double-invoke — the timer is always (re)scheduled so the redirect survives
  // StrictMode's mount→cleanup→remount cycle in dev.
  const trackedRef = useRef(false);
  useEffect(() => {
    if (!trackedRef.current) {
      trackedRef.current = true;
      analytics.trackExtensionUninstalled();
    }

    const timer = setTimeout(() => {
      window.location.replace(FEEDBACK_FORM_URL);
    }, REDIRECT_DELAY_MS);
    return () => clearTimeout(timer);
  }, []);

  return (
    // Background matches public/index.html's gradient exactly, so the static
    // paint and the React render are visually identical — no flash on boot.
    <div
      className="min-h-screen relative text-white antialiased flex flex-col items-center justify-center px-6 overflow-hidden"
      style={{ background: "linear-gradient(to bottom right, #000000, #0a0a0a, #000000)" }}
    >
      {/* Faint top-center brand wash — barely there, adds depth without noise */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 h-[520px] w-[820px] rounded-full bg-gradient-to-b from-cyan-500/[0.12] via-blue-500/[0.06] to-transparent blur-3xl" />

      <div className="relative flex flex-col items-center text-center animate-zoom-in">
        {/* Waving badge */}
        <div className="relative mb-8">
          <div className="absolute -inset-3 rounded-[26px] bg-gradient-to-br from-cyan-500/25 to-blue-500/15 blur-xl" />
          <div className="relative flex h-[88px] w-[88px] items-center justify-center rounded-3xl bg-white/[0.04] ring-1 ring-white/10 shadow-lg text-5xl">
            <span className="animate-bounce-slow">👋</span>
          </div>
        </div>

        <h1 className="text-[2.25rem] leading-[1.1] sm:text-5xl font-semibold tracking-tight text-white">
          Sad to see you go
        </h1>
        <p className="mt-4 text-lg text-gray-400 max-w-md leading-relaxed">
          Taking you to a quick form — we'd love to hear what made you leave.
        </p>

        {/* Loading indicator */}
        <div className="mt-10 flex items-center gap-3 text-sm font-medium text-gray-500">
          <span className="h-4 w-4 rounded-full border-2 border-white/15 border-t-white animate-spin" />
          Redirecting…
        </div>

        {/* Fallback if the redirect is blocked or slow */}
        <p className="mt-6 text-[13px] text-gray-500">
          Not redirected?{" "}
          <a
            href={FEEDBACK_FORM_URL}
            className="font-medium text-white underline underline-offset-2 hover:text-gray-300"
          >
            Open the form
          </a>
        </p>
      </div>
    </div>
  );
};

export default UninstallFeedback;
